import React, { useCallback, useEffect, useRef, useState } from 'react';
import quizApi from '../utils/quizApi';

const DIFFICULTIES = [
  { id: 'easy', label: 'Easy', hint: 'Recall — facts stated plainly in the document.' },
  { id: 'medium', label: 'Medium', hint: 'Application — what to do in a described situation.' },
  { id: 'hard', label: 'Hard', hint: 'Judgement — realistic scenarios with a deciding detail.' },
];

/**
 * Write a quiz from the document's own file.
 *
 * The revision is NOT a choice here. It is the revision the quiz is already filed
 * under, because a quiz is evidence that people were tested on a particular
 * revision — so the text the questions come from and the revision the quiz claims
 * to cover have to be the same thing by construction. The dialog shows it instead.
 *
 * Three phases in one window: the settings, a live progress readout while Orcanos
 * is read and the model runs, and then what actually happened — how the revision
 * differs from the one before it, and how many questions were added.
 */
function GenerateQuizModal({ quiz, onClose, onDone }) {
  const [phase, setPhase] = useState('form');          // form | running | done
  const [error, setError] = useState('');

  const [questions, setQuestions] = useState(10);
  const [difficulty, setDifficulty] = useState('medium');
  const [optionsPer, setOptionsPer] = useState(4);
  const [allowMulti, setAllowMulti] = useState(false);
  const [attempts, setAttempts] = useState(quiz?.max_attempts ?? 3);
  const [focusChanges, setFocusChanges] = useState(true);

  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  const [result, setResult] = useState(null);

  const timer = useRef(null);
  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };
  useEffect(() => stop, []);

  const poll = useCallback(async (id) => {
    try {
      const d = await quizApi.generateStatus(quiz.id, id);
      setProgress(d.progress || 0);
      setStage(d.result?.stage || '');
      if (d.status === 'completed') {
        stop();
        setResult(d.result || {});
        setPhase('done');
      } else if (d.status === 'error' || d.status === 'cancelled') {
        stop();
        setError(d.error || 'The run stopped before it finished.');
        setResult(d.result || null);
        setPhase('done');
      }
    } catch (e) {
      stop();
      setError(e.message);
      setPhase('done');
    }
  }, [quiz.id]);

  const start = async () => {
    setError('');
    setPhase('running');
    setProgress(0);
    setStage('Starting…');
    try {
      const d = await quizApi.generate(quiz.id, {
        questions: Number(questions),
        difficulty,
        options_per_question: Number(optionsPer),
        allow_multi: allowMulti,
        focus_changes: focusChanges,
        max_attempts: Number(attempts),
      });
      setJobId(d.job_id);
      timer.current = setInterval(() => poll(d.job_id), 1500);
      poll(d.job_id);
    } catch (e) {
      setError(e.message);
      setPhase('form');
    }
  };

  const close = () => {
    stop();
    // The run is a server-side job; closing this window only stops the watching.
    // Anything already added to the quiz stays added, so the editor must reload.
    onClose(Boolean(result?.questions_added));
  };

  const finish = () => {
    stop();
    onDone(result || {});
  };

  const diff = result?.diff;
  const warnings = result?.warnings || [];

  return (
    <div className="qz-gen-overlay" onClick={close}>
      <div className="qz-gen" onClick={(e) => e.stopPropagation()}>
        <div className="qz-gen-head">
          <h3>Generate quiz</h3>
          <button className="qz-gen-x" onClick={close} title="Close">×</button>
        </div>

        <div className="qz-gen-body">
          {/* The document and revision are stated, never chosen — see the note above. */}
          <div className="qz-gen-src">
            <div>
              <b>{quiz.doc_name || quiz.doc_key}</b>
              <div className="qz-meta">
                <span className="qz-key">{quiz.doc_key}</span>
                {quiz.revision ? <> · revision <span className="qz-pill qz-pill-rev">{quiz.revision}</span></> : null}
              </div>
            </div>
            <div className="qz-meta qz-gen-src-note">
              Questions are written from the file attached to this revision — the same
              revision the training report says people owe.
            </div>
          </div>

          {error && <div className="qz-note qz-note-err"><span>{error}</span></div>}

          {phase === 'form' && (
            <>
              <div className="qz-gen-row">
                <div className="qz-field">
                  <label>Questions</label>
                  <input type="number" min="1" max="50" className="qz-input"
                         value={questions}
                         onChange={(e) => setQuestions(e.target.value)} />
                  <div className="qz-meta">Fewer good questions beat many vague ones.</div>
                </div>
                <div className="qz-field">
                  <label>Answers per question</label>
                  <input type="number" min="2" max="6" className="qz-input"
                         value={optionsPer}
                         onChange={(e) => setOptionsPer(e.target.value)} />
                  <div className="qz-meta">Two is a true/false; beyond six they pad.</div>
                </div>
                <div className="qz-field">
                  <label>Attempts allowed</label>
                  <input type="number" min="1" max="20" className="qz-input"
                         value={attempts}
                         onChange={(e) => setAttempts(e.target.value)} />
                  <div className="qz-meta">This is the quiz’s own retry rule.</div>
                </div>
              </div>

              <div className="qz-field">
                <label>Difficulty</label>
                <div className="qz-gen-seg">
                  {DIFFICULTIES.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className={`qz-gen-segbtn${difficulty === d.id ? ' on' : ''}`}
                      onClick={() => setDifficulty(d.id)}
                    >{d.label}</button>
                  ))}
                </div>
                <div className="qz-meta">
                  {DIFFICULTIES.find((d) => d.id === difficulty)?.hint}
                </div>
              </div>

              <label className="qz-gen-check">
                <input type="checkbox" checked={allowMulti}
                       onChange={(e) => setAllowMulti(e.target.checked)} />
                <span>
                  <b>Allow more than one right answer</b>
                  <div className="qz-meta">
                    Off by default: every question has exactly one correct answer. Multiple-answer
                    questions are marked <b>all or nothing</b> — every correct option must be
                    picked and no incorrect one — so they are markedly harder than they look.
                  </div>
                </span>
              </label>

              <label className="qz-gen-check">
                <input type="checkbox" checked={focusChanges}
                       onChange={(e) => setFocusChanges(e.target.checked)} />
                <span>
                  <b>Focus on what changed in this revision</b>
                  <div className="qz-meta">
                    Compares this revision’s file with the one before it and weights the
                    questions toward the differences — which is usually why people are being
                    retrained. The comparison is shown either way.
                  </div>
                </span>
              </label>

              <div className="qz-note qz-note-info">
                <span>ℹ</span>
                <div>
                  Generated questions are <b>added to</b> this quiz and marked <b>AI draft</b>.
                  Nothing is published: you review and edit them first. Reading a controlled
                  file is recorded in the document’s Orcanos history, as it is for anyone
                  who opens it.
                </div>
              </div>
            </>
          )}

          {phase === 'running' && (
            <div className="qz-gen-run">
              <div className="qz-progress">
                <div className="qz-bar" style={{ width: `${Math.max(progress, 4)}%` }} />
              </div>
              <div className="qz-progress-lab">{stage || 'Working…'}</div>
              <div className="qz-meta">
                Reading the document out of Orcanos and writing questions — this usually
                takes under a minute. Closing this window stops the progress display, not
                the run.
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div className="qz-gen-done">
              {result?.questions_added > 0 && (
                <div className="qz-note qz-note-ok">
                  <span>✓</span>
                  <div>
                    <b>{result.questions_added} question{result.questions_added === 1 ? '' : 's'} added</b>
                    {' '}as AI drafts. Review and edit them before publishing.
                  </div>
                </div>
              )}

              {diff && (
                <div className="qz-panel qz-gen-diff">
                  <div className="qz-panel-head">
                    What changed since revision {diff.previous_revision || '—'}
                  </div>
                  <div className="qz-panel-body">
                    {diff.headline && <p className="qz-gen-headline">{diff.headline}</p>}
                    {diff.no_substantive_change ? (
                      <div className="qz-meta">
                        Nothing substantive changed between these revisions — only formatting
                        or document-control details. Worth asking whether this revision needs
                        retraining at all.
                      </div>
                    ) : (
                      <ul className="qz-gen-changes">
                        {(diff.changes || []).map((c, i) => (
                          <li key={i}>
                            <b>{c.area}</b> — {c.what}
                            {c.matters ? <div className="qz-meta">{c.matters}</div> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {warnings.length > 0 && (
                <div className="qz-note qz-note-warn">
                  <span>⚠</span>
                  <div>
                    <ul className="qz-gen-warns">
                      {warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="qz-gen-foot">
          {phase === 'form' && (
            <>
              <button className="qz-btn qz-btn-ghost" onClick={close}>Cancel</button>
              <button className="qz-btn qz-btn-primary" onClick={start}>Generate</button>
            </>
          )}
          {phase === 'running' && (
            <button className="qz-btn qz-btn-ghost" onClick={close}>Close</button>
          )}
          {phase === 'done' && (
            <button className="qz-btn qz-btn-primary" onClick={finish}>
              {result?.questions_added > 0 ? 'Review questions' : 'Close'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default GenerateQuizModal;
